import React, { useState, useLayoutEffect } from "react";
import { View, ActivityIndicator } from "react-native";
import { useNavigation } from "@react-navigation/native";
import { router, useGlobalSearchParams, useRootNavigationState } from "expo-router";
import { useTheme } from "@/hooks/useTheme";
import { ProjectType } from "@/types/db/projects";
import { RelatedShiftsScreen } from "@/components/screens/shared/RelatedShiftsScreen";
import { RelatedEstimatesScreen } from "@/components/screens/shared/RelatedEstimatesScreen";
import { RelatedInvoicesScreen } from "@/components/screens/shared/RelatedInvoicesScreen";
import { RelatedTimecardsScreen } from "@/components/screens/shared/RelatedTimecardsScreen";
import { RelatedExpensesScreen } from "@/components/screens/shared/RelatedExpensesScreen";
import { RelatedIncidentsScreen } from "@/components/screens/shared/RelatedIncidentsScreen";
import { SmallToggle } from "@/components/SmallToggle";
import DrawerNavigator, { Drawer } from "@/components/screens/shared/DrawerNavigator";
import useAuth from "@/hooks/useAuth";
import usePermissions from "@/hooks/usePermissions";

export interface ProjectRelatedTabProps {
  project: ProjectType | null;
  loading?: boolean;
}

// Wrapper component for Shifts screen (no toggle - show all shifts for project)
function ShiftsScreenWithHeader() {
  const { displayNames } = useAuth();
  const navigation = useNavigation();

  useLayoutEffect(() => {
    navigation.setOptions({
      title: displayNames?.shifts?.plural || "Shifts",
    });
  }, [navigation, displayNames]);

  return <RelatedShiftsScreen showInactive={true} />;
}

// Wrapper component for Time Cards screen with header toggle
function TimecardsScreenWithHeader() {
  const { colors } = useTheme();
  const { displayNames } = useAuth();
  const navigation = useNavigation();
  const [showApproved, setShowApproved] = useState(true);

  useLayoutEffect(() => {
    navigation.setOptions({
      title: displayNames?.time_cards?.plural || "Time Cards",
      headerRight: () => (
        <View style={{ marginRight: 16 }}>
          <SmallToggle value={showApproved} onValueChange={setShowApproved} label="Show Approved" />
        </View>
      ),
    });
  }, [navigation, showApproved, colors, displayNames]);

  return <RelatedTimecardsScreen showApproved={showApproved} />;
}

// Wrapper for Incidents screen
function IncidentsScreenWithHeader() {
  const { displayNames } = useAuth();
  const navigation = useNavigation();

  useLayoutEffect(() => {
    navigation.setOptions({
      title: displayNames?.incidents?.plural || "Incidents",
    });
  }, [navigation, displayNames]);

  return <RelatedIncidentsScreen />;
}

export function ProjectRelatedTab({ project, loading = false }: ProjectRelatedTabProps) {
  const { loading: authLoading, displayNames } = useAuth();
  const { shifts, time_cards, estimates, invoices, expenses, incidents } = usePermissions();
  const { colors } = useTheme();
  const params = useGlobalSearchParams<{ related?: string }>();
  const rootNavigationState = useRootNavigationState();

  // Wait for auth to load before rendering navigator
  if (authLoading) {
    return (
      <View style={{ flex: 1, justifyContent: "center", alignItems: "center" }}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  // Build available screens based on permissions
  const availableScreens: string[] = [];
  if (shifts.read) availableScreens.push("Shifts");
  if (time_cards.read) availableScreens.push("Timecards");
  if (estimates.read) availableScreens.push("Estimates");
  if (invoices.read) availableScreens.push("Invoices");
  if (expenses.read) availableScreens.push("Expenses");
  if (incidents.read) availableScreens.push("Incidents");

  // Validate requested route exists
  const getValidInitialRoute = (requested?: string): string | undefined => {
    if (!requested) return undefined;
    return availableScreens.find((s) => s.toLowerCase() === requested.toLowerCase());
  };

  const handleScreenChange = (screenName: string) => {
    // Only set params after Root Layout is mounted
    if (!rootNavigationState?.key) return;
    router.setParams({ related: screenName });
  };

  return (
    <DrawerNavigator
      initialRouteName={getValidInitialRoute(params.related)}
      onScreenChange={handleScreenChange}
      entityType="project"
    >
      {shifts.read && (
        <Drawer.Screen
          name="Shifts"
          component={ShiftsScreenWithHeader}
          options={{ title: displayNames?.shifts?.plural || "Shifts" }}
        />
      )}
      {time_cards.read && (
        <Drawer.Screen
          name="Timecards"
          component={TimecardsScreenWithHeader}
          options={{ title: displayNames?.time_cards?.plural || "Timecards" }}
        />
      )}
      {estimates.read && (
        <Drawer.Screen
          name="Estimates"
          component={RelatedEstimatesScreen}
          options={{ title: displayNames?.estimates?.plural || "Estimates" }}
        />
      )}
      {invoices.read && (
        <Drawer.Screen
          name="Invoices"
          component={RelatedInvoicesScreen}
          options={{ title: displayNames?.invoices?.plural || "Invoices" }}
        />
      )}
      {expenses.read && (
        <Drawer.Screen
          name="Expenses"
          component={RelatedExpensesScreen}
          options={{ title: displayNames?.expenses?.plural || "Expenses" }}
        />
      )}
      {incidents.read && (
        <Drawer.Screen
          name="Incidents"
          component={IncidentsScreenWithHeader}
          options={{ title: displayNames?.incidents?.plural || "Incidents" }}
        />
      )}
    </DrawerNavigator>
  );
}
